import { memo, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, Grid } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormTextField from 'components/ui/form/FormTextField';
import FormDateField from 'components/ui/form/FormDateField';
import FormSelectField from 'components/ui/form/FormSelectField';
import useFormSubmit from 'hooks/useFormSubmit';

const validationSchema = yup.object({
  till_date: yup.string().required('tasks.staffTask.validation.tillDate'),
  user_id: yup.number().required('tasks.staffTask.validation.userId'),
});

const TaskEditModal = ({ open, setOpen, task, setTask, refetchFn }) => {
  const { t } = useTranslation();
  const { submit, isSubmitting } = useFormSubmit();

  const formik = useFormik({
    initialValues: {
      till_date: '',
      title: '',
      user_id: '',
    },
    validationSchema,
    onSubmit: (values) => {
      submit(
        { type: 'post', contentType: 'simple' },
        values,
        `/admin/task-staff/update/${task?.id}`,
        { title: t('tasks.staffTask.alerts.updated') },
        null,
        true,
        handleFinish
      );
    },
  });

  useEffect(() => {
    if (task?.id) {
      formik.setValues({
        till_date: task.till_date || '',
        title: task.title || '',
        user_id: task.user_id || '',
      });
    }
  }, [task]);

  const handleFinish = () => {
    refetchFn();
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    setTask(null);
    formik.resetForm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ className: '!base-border !shadow-none' }}
      disableEscapeKeyDown
    >
      <DialogTitle className='!bg-gray-100 !mb-2 text-center'>
        {t('tasks.staffTask.editModal.title')}
      </DialogTitle>

      <DialogContent>
        <form onSubmit={formik.handleSubmit} className='pt-2'>
          <Grid container spacing={2} rowSpacing={1} columns={{ xs: 12 }}>
            <Grid item xs={12}>
              <FormTextField
                delay={0}
                label={t('common.table.name')}
                fieldName='title'
                formik={formik}
              />
            </Grid>

            <Grid item xs={12}>
              <FormDateField
                delay={0.1}
                label={t('common.fields.endDate')}
                fieldName='till_date'
                formik={formik}
              />
            </Grid>

            <Grid item xs={12}>
              <FormSelectField
                delay={0.2}
                fieldName='user_id'
                label={t('common.fields.staff')}
                formik={formik}
                path='/dictionary/staffes'
                isitemsizesmall
              />
            </Grid>

            <Grid item xs={12}>
              <FormActionButtons
                delay={0.3}
                isSubmitting={isSubmitting}
                formType='dialog'
                setOpen={setOpen}
                reset={formik.resetForm}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default memo(TaskEditModal);
